/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useCallback } from "react";

import type { Room } from "matrix-js-sdk";
import { JoinRule } from "matrix-js-sdk";
import { EventType } from "matrix-js-sdk/src/@types/event";
import { useRoomState } from "./useRoomState";

/**
 * Хук для определения, можно ли делиться ссылкой на комнату.
 * Комната доступна для шаринга если:
 * 1. Правило присоединения = "public"
 * 2. ИЛИ у комнаты есть публичный алиас (canonical alias)
 * 3. ИЛИ комната не зашифрована (такие комнаты создаются как публичные)
 * 4. ИЛИ history_visibility = "world_readable" или "shared"
 *
 * @param room - комната Matrix
 * @returns true если можно делиться ссылкой
 */
export function useCanShareRoom(room: Room): boolean {
  return useRoomState(
    room,
    useCallback(
      (state) => {
        // 1. Проверяем join_rule
        const joinRule = state.getJoinRule();
        if (joinRule === JoinRule.Public) {
          return true;
        }

        // 2. Проверяем наличие публичного алиаса
        const canonicalAlias = room.getCanonicalAlias();
        if (canonicalAlias) {
          return true;
        }

        // 3. Проверяем, не зашифрована ли комната
        // Незашифрованные комнаты обычно публичные
        const hasEncryption = room.hasEncryptionStateEvent();
        if (!hasEncryption) {
          return true;
        }

        // 4. Проверяем history_visibility
        const historyVisibilityEvent = state.getStateEvents(
          EventType.RoomHistoryVisibility,
          "",
        );
        const historyVisibility = historyVisibilityEvent?.getContent()
          ?.history_visibility as string | undefined;

        if (
          historyVisibility === "world_readable" ||
          historyVisibility === "shared"
        ) {
          return true;
        }

        // Если ни один из критериев не подошел, значит это приватная комната
        // только по приглашениям
        return false;
      },
      [room],
    ),
  );
}
